/**
 * AuthGuard Component
 * Protects routes and components based on authentication state and permissions
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Permission } from '../../../contracts/types';
import LoginForm from './LoginForm';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  redirectTo?: string;
  showLogin?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredPermissions = [],
  requireAll = true,
  fallback,
  redirectTo,
  showLogin = true,
}) => {
  const authContext = useAuth() as any;
  const {
    isAuthenticated,
    isLoading,
    user,
  } = authContext;

  // Permission functions may not exist in simpler AuthContext - provide defaults
  const hasAnyPermission = authContext.hasAnyPermission || (() => true);
  const hasAllPermissions = authContext.hasAllPermissions || (() => true);

  // Show loading state while authentication is being determined
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (!isAuthenticated || !user) {
    if (showLogin) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <LoginForm
            onSuccess={() => {
              if (redirectTo) {
                window.location.href = redirectTo;
              }
            }}
          />
        </div>
      );
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to access this content.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Check permissions if required
  if (requiredPermissions.length > 0) {
    const hasRequiredPermissions = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasRequiredPermissions) {
      if (fallback) {
        return <>{fallback}</>;
      }

      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You don't have sufficient permissions to access this content.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
              <h3 className="font-medium text-yellow-800 mb-2">Required Permissions:</h3>
              <ul className="text-sm text-yellow-700">
                {requiredPermissions.map(permission => (
                  <li key={permission} className="mb-1">
                    • {permission.replace(':', ' ').replace('_', ' ')}
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // Render children if all checks pass
  return <>{children}</>;
};

/**
 * Higher-order component for route protection
 */
export function withAuthGuard<P extends object>(
  Component: React.ComponentType<P>,
  guardProps?: Omit<AuthGuardProps, 'children'>
) {
  return function AuthGuardedComponent(props: P) {
    return (
      <AuthGuard {...guardProps}>
        <Component {...props} />
      </AuthGuard>
    );
  };
}

/**
 * Permission-based conditional rendering component
 */
interface PermissionGateProps {
  children: React.ReactNode;
  permissions: Permission[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  user?: any; // Allow override for testing
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permissions,
  requireAll = true,
  fallback = null,
  user: overrideUser,
}) => {
  const authContext = useAuth() as any;
  const { user: authUser } = authContext;
  const user = overrideUser || authUser;

  // Permission functions may not exist - provide defaults that allow access
  const hasAnyPermission = authContext.hasAnyPermission || (() => true);
  const hasAllPermissions = authContext.hasAllPermissions || (() => true);

  if (!user) {
    return <>{fallback}</>;
  }

  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Role-based conditional rendering component
 */
interface RoleGateProps {
  children: React.ReactNode;
  allowedRoles: string[];
  fallback?: React.ReactNode;
  user?: any; // Allow override for testing
}

export const RoleGate: React.FC<RoleGateProps> = ({
  children,
  allowedRoles,
  fallback = null,
  user: overrideUser,
}) => {
  const { user: authUser } = useAuth();
  const user = overrideUser || authUser;

  if (!user || !allowedRoles.includes(user.role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Organization-based conditional rendering component
 */
interface OrganizationGateProps {
  children: React.ReactNode;
  allowedOrganizations?: string[];
  fallback?: React.ReactNode;
  user?: any; // Allow override for testing
}

export const OrganizationGate: React.FC<OrganizationGateProps> = ({
  children,
  allowedOrganizations,
  fallback = null,
  user: overrideUser,
}) => {
  const { user: authUser } = useAuth();
  const user = overrideUser || authUser;

  if (!user || !user.organization) {
    return <>{fallback}</>;
  }

  if (allowedOrganizations && !allowedOrganizations.includes(user.organization.org_id)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default AuthGuard;